import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { createPortal } from 'react-dom';
import { useSfx } from '@/lib/use-sfx';
import { useMotionPrefs } from '@/lib/use-motion-prefs';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  className?: string;
}

/**
 * Bottom sheet used for popovers and confirmations. Traps focus, closes on
 * Escape and on backdrop tap, and respects reduced motion.
 */
export function Sheet({ open, onClose, children, title, className }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { reduced } = useMotionPrefs();
  const sfx = useSfx();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    // Prevent the page behind from scrolling while the sheet is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    // Very quiet, per plan doc 15 — a sheet that announces itself every time
    // becomes the loudest thing in the app.
    sfx.whoosh();
  }, [open, sfx]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <m.button
            type="button"
            aria-label={t('common.close')}
            className="absolute inset-0 bg-[var(--overlay)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0.01 : 0.2 }}
            onClick={onClose}
          />
          <m.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={cn(
              'relative w-full max-w-[var(--content-max)] rounded-t-[var(--radius-xl)]',
              'bg-surface px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]',
              // A tall sheet on a short phone scrolls rather than pushing its
              // buttons off the bottom of the screen.
              'max-h-[85dvh] overflow-y-auto overscroll-contain',
              'shadow-soft-lg outline-none',
              className,
            )}
            initial={{ y: reduced ? 0 : '100%', opacity: reduced ? 0 : 1 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: reduced ? 0 : '100%', opacity: reduced ? 0 : 1 }}
            // A short, deterministic ease rather than a long spring: a sheet
            // that is still sliding under a thumb gets mis-tapped.
            transition={reduced ? { duration: 0.01 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div aria-hidden className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-line" />
            {title ? <h2 className="mb-3 text-xl">{title}</h2> : null}
            {children}
          </m.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
